import { atom } from 'jotai'
import { match } from 'ts-pattern'
import {
  format,
  isAfter,
  isBefore,
  type Duration
} from '@sequencehq/utils/dates'
import { Currency } from '@sequencehq/api/commonEnums'
import { CubeReducerState } from 'modules/Cube/domain/cube.domain.types'
import { BillingSchedule } from 'modules/Customer/hooks/useFetchBillingSchedules'
import { LoadedBillingScheduleData } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/useLoadBillingScheduleEditor'
import { validateCombinedSchedule } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/validateCombinedSchedule'
import {
  deriveScheduleMetadata,
  EligibleSchedule
} from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/deriveScheduleMetadata'
import { formatContractLength } from '@sequencehq/utils'
import { getFixedEndRenewOnDate } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/getFixedEndRenewOnDate'
import { getRenewalStartMinDate } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/getRenewalStartMinDate'
import { buildRenewalReview } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/buildRenewalReview'
import { phaseDateFormat } from 'modules/Cube/cube.constants'
import type { QuoteExecuteScheduleAction } from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'

export type RenewalScheduleMode = 'new' | 'existing'

/** An eligible schedule with its full data loaded, ready to be validated. */
export type LoadedEligibleSchedule = {
  schedule: BillingSchedule
  loaded: LoadedBillingScheduleData
}

/**
 * The cube-derived context the modal needs to validate and describe the renewal.
 * Hydrated from the cube by the modal's colocated hook (atoms can't read hooks).
 */
export type RenewalContext = {
  customerId: string
  currency: Currency | undefined
  totalPricingDuration: Duration | undefined
  quoteData: CubeReducerState['data']
  configuration: CubeReducerState['configuration']
}

// --- Base atoms ---
// Related state is grouped into one atom per area so several fields can be set
// in a single write (fewer renders, and no half-updated intermediate state —
// e.g. loaded schedules appearing while still flagged as loading). The base
// atoms are private; individual values are read through the selectors below and
// set through the focused writable atoms or the batched writers.

type RenewalData = {
  context: RenewalContext | null
  loadedSchedules: LoadedEligibleSchedule[]
  isLoadingSchedules: boolean
}

const renewalDataAtom = atom<RenewalData>({
  context: null,
  loadedSchedules: [],
  // Start in the loading state so the modal shows a spinner until the first
  // real load resolves, rather than briefly flashing an empty selection.
  isLoadingSchedules: true
})

type RenewalForm = {
  mode: RenewalScheduleMode
  selectedScheduleId: string | undefined
  /** The renewal date chosen when adding to an existing schedule. */
  renewalDate: Date | undefined
  /** The start date chosen when creating a new schedule. */
  newScheduleStartDate: Date | undefined
  /** Whether creating a new schedule also ends the selected existing one. */
  endExistingSchedule: boolean
}

const renewalFormAtom = atom<RenewalForm>({
  mode: 'new',
  selectedScheduleId: undefined,
  renewalDate: undefined,
  newScheduleStartDate: undefined,
  endExistingSchedule: false
})

// --- Batched writers: set several fields of a group in a single atom write ---

/** Hydrates the loading/data group; the colocated modal hook uses this to set
 * the loaded schedules and loading flag together as the load resolves. When the
 * loaded schedules are (re)populated, the selection is preset to the first
 * (earliest-starting) one so it always matches the available schedules, and the
 * renewal date defaults from that preselection like any other selection. */
export const hydrateRenewalDataAtom = atom(
  null,
  (get, set, patch: Partial<RenewalData>) => {
    set(renewalDataAtom, { ...get(renewalDataAtom), ...patch })
    if (patch.loadedSchedules) {
      const preselectedSchedule = patch.loadedSchedules[0]?.schedule
      set(renewalFormAtom, {
        ...get(renewalFormAtom),
        selectedScheduleId: preselectedSchedule?.id,
        renewalDate: getFixedEndRenewOnDate(preselectedSchedule)
      })
    }
  }
)

/** Sets one or more form fields in a single write (used by the modal hook for
 * the selection defaults). */
export const updateRenewalFormAtom = atom(
  null,
  (get, set, patch: Partial<RenewalForm>) =>
    set(renewalFormAtom, { ...get(renewalFormAtom), ...patch })
)

// --- Selectors over the data group ---

export const renewalContextAtom = atom(get => get(renewalDataAtom).context)
export const loadedSchedulesAtom = atom(
  get => get(renewalDataAtom).loadedSchedules
)

// --- Selectors over the form group ---

export const renewalModeAtom = atom(get => get(renewalFormAtom).mode)
export const changeRenewalModeAtom = atom(
  null,
  (get, set, next: RenewalScheduleMode) =>
    set(renewalFormAtom, { ...get(renewalFormAtom), mode: next })
)

/** Writable selectors: read one field, write patches just that field. */
export const selectedScheduleIdAtom = atom(
  get => get(renewalFormAtom).selectedScheduleId,
  (get, set, next: string | undefined) => {
    const nextSchedule = get(loadedSchedulesAtom).find(
      ({ schedule }) => schedule.id === next
    )?.schedule
    set(renewalFormAtom, {
      ...get(renewalFormAtom),
      selectedScheduleId: next,
      // Selecting a schedule re-defaults the renewal date to the day after it
      // ends. An open-ended schedule has no end, so the date clears for an
      // explicit choice rather than carrying over the previous schedule's date.
      renewalDate: getFixedEndRenewOnDate(nextSchedule)
    })
  }
)
export const renewalDateAtom = atom(
  get => get(renewalFormAtom).renewalDate,
  (get, set, next: Date | undefined) =>
    set(renewalFormAtom, { ...get(renewalFormAtom), renewalDate: next })
)
export const newScheduleStartDateAtom = atom(
  get => get(renewalFormAtom).newScheduleStartDate,
  (get, set, next: Date | undefined) =>
    set(renewalFormAtom, {
      ...get(renewalFormAtom),
      newScheduleStartDate: next
    })
)

export const endExistingScheduleAtom = atom(
  get => get(renewalFormAtom).endExistingSchedule,
  (get, set, next: boolean) =>
    set(renewalFormAtom, {
      ...get(renewalFormAtom),
      endExistingSchedule: next
    })
)

// --- Selector atom: map the loaded schedules to their applicability metadata ---

export const eligibleSchedulesAtom = atom<EligibleSchedule[]>(get => {
  const context = get(renewalContextAtom)
  if (!context) {
    return []
  }
  const { quoteData, configuration } = context
  // The chosen renewal date closes an open-ended schedule's final phase so it's
  // validated by the same proration check as a fixed-end one (no-op otherwise).
  const renewalStartDate = get(renewalDateAtom)
  return get(loadedSchedulesAtom).map(({ schedule, loaded }) =>
    deriveScheduleMetadata({
      schedule,
      validation: {
        asIs: validateCombinedSchedule({
          loadedSchedule: loaded,
          quoteData,
          configuration,
          resetBillingPeriod: false,
          renewalStartDate
        }),
        withReset: validateCombinedSchedule({
          loadedSchedule: loaded,
          quoteData,
          configuration,
          resetBillingPeriod: true,
          renewalStartDate
        })
      }
    })
  )
})

// --- Derived atoms ---

export const selectedScheduleAtom = atom<EligibleSchedule | undefined>(get =>
  get(eligibleSchedulesAtom).find(
    ({ schedule }) => schedule.id === get(selectedScheduleIdAtom)
  )
)

/** The renewal date the modal acts on: the chosen date, or (for a fixed-end
 * schedule with no choice) the day after it ends. */
export const renewalStartDateAtom = atom<Date | undefined>(
  get =>
    get(renewalDateAtom) ??
    getFixedEndRenewOnDate(get(selectedScheduleAtom)?.schedule)
)

export const renewalStartMinDateAtom = atom<Date | undefined>(get =>
  getRenewalStartMinDate(get(selectedScheduleAtom)?.schedule, new Date())
)

/** The latest the renewal may begin: the day after a fixed-end schedule ends
 * (open-ended schedules have no ceiling). */
export const renewalStartMaxDateAtom = atom<Date | undefined>(get =>
  getFixedEndRenewOnDate(get(selectedScheduleAtom)?.schedule)
)

/** Field-level validation errors for the chosen renewal date, so the date field
 * renders a standard error when the date is out of the allowed range. */
export const renewalDateValidationErrorsAtom = atom<{ message: string }[]>(
  get => {
    const renewalDate = get(renewalDateAtom)
    if (!renewalDate) {
      return []
    }
    const minDate = get(renewalStartMinDateAtom)
    const maxDate = get(renewalStartMaxDateAtom)
    if (minDate && isBefore(renewalDate, minDate)) {
      return [
        {
          message: `Renewal start date cannot be before the start of the active schedule phase (${format(minDate, phaseDateFormat)})`
        }
      ]
    }
    if (maxDate && isAfter(renewalDate, maxDate)) {
      return [
        {
          message: `Renewal start date cannot be after the end of the schedule ${format(maxDate, phaseDateFormat)}`
        }
      ]
    }
    return []
  }
)

export const resetBillingDayLabelAtom = atom(get => {
  const renewalStartDate = get(renewalStartDateAtom)
  return renewalStartDate ? format(renewalStartDate, 'do') : undefined
})

export const renewalReviewAtom = atom(get => {
  const selected = get(selectedScheduleAtom)
  const renewalStartDate = get(renewalStartDateAtom)
  const context = get(renewalContextAtom)
  if (!selected || !renewalStartDate || !context) {
    return undefined
  }
  return buildRenewalReview({
    schedule: selected.schedule,
    customerId: context.customerId,
    renewalStartDate,
    totalPricingDuration: context.totalPricingDuration,
    duration: formatContractLength(context.totalPricingDuration)
  })
})

export const isRenewalLoadingAtom = atom(
  get => get(renewalDataAtom).isLoadingSchedules
)

/** Whether "add to existing schedule" can be chosen: only when the customer has
 * at least one schedule that can actually take the renewal. */
export const hasApplicableScheduleAtom = atom(get =>
  get(eligibleSchedulesAtom).some(schedule => schedule.isViableSchedule)
)

/** How many eligible schedules can take the renewal without needing a reset. */
export const validScheduleCountAtom = atom(
  get =>
    get(eligibleSchedulesAtom).filter(
      schedule => schedule.isViableSchedule && !schedule.resetRequired
    ).length
)

export const resetBillingPeriodAtom = atom(
  get => get(selectedScheduleAtom)?.resetRequired ?? false
)

export const scheduleActionAtom = atom<QuoteExecuteScheduleAction | undefined>(
  get =>
    match(get(renewalModeAtom))
      .returnType<QuoteExecuteScheduleAction | undefined>()
      .with('new', () => {
        const billingScheduleId = get(selectedScheduleIdAtom)
        if (get(endExistingScheduleAtom) && billingScheduleId) {
          return { mode: 'REPLACE_EXISTING', billingScheduleId }
        }

        return { mode: 'CREATE_NEW' }
      })
      .with('existing', () => {
        const billingScheduleId = get(selectedScheduleIdAtom)
        if (!billingScheduleId) {
          return undefined
        }

        return {
          mode: 'ADD_TO_EXISTING',
          billingScheduleId,
          phaseRecurrencePreference: get(resetBillingPeriodAtom)
            ? 'RESET'
            : 'CONTINUE_FROM_PREVIOUS_PHASE'
        }
      })
      .exhaustive()
)

/**
 * Whether the "add to existing" selection can't proceed: milestone schedules
 * can't be renewed; any schedule that fails the combined-schedule validation
 * (non-viable) is blocked; open-ended schedules additionally gate on picking a
 * renewal date. The renewal date is optional for a fixed-end schedule but must
 * be within [min, max].
 */
export const isExistingSelectionBlockedAtom = atom(get => {
  if (get(isRenewalLoadingAtom)) {
    return true
  }
  const selected = get(selectedScheduleAtom)
  if (
    !selected ||
    selected.isMilestone ||
    get(renewalDateValidationErrorsAtom).length > 0
  ) {
    return true
  }
  // A non-viable schedule (fails combined validation even with a reset) can
  // never proceed, regardless of shape. Open-ended schedules additionally gate
  // on picking a renewal date.
  if (!selected.isViableSchedule) {
    return true
  }
  return selected.isOpenEnded && !get(renewalStartDateAtom)
})
